import { useEffect, useState } from "react";
import { ShieldCheck, UploadCloud, AlertTriangle, Download, RotateCw, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import AccountingReviewDrawer from "@/components/AccountingReviewDrawer";
import { fmtDateTime } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const TABS = [
  { value: "pending_review", label: "Pending Review" },
  { value: "needs_info", label: "Needs Info" },
  { value: "ready_for_sync", label: "Ready for Sync" },
  { value: "synced", label: "Synced" },
  { value: "excluded", label: "Excluded" },
  { value: "", label: "All" },
];

const SOURCE_LABELS = {
  po: "PO", outside_purchase: "Outside", corpay: "CorPay", ramp: "Ramp", csv: "CSV", invoice: "Invoice",
};

export default function AccountingQueue() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("pending_review");
  const [source, setSource] = useState("");
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [vendor, setVendor] = useState("");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("queue"); // queue | issues | export
  const [issuesKey, setIssuesKey] = useState(0);
  const [accountingMode, setAccountingMode] = useState("qbo");
  const [syncingAll, setSyncingAll] = useState(false);

  const load = () => {
    const params = {};
    if (status) params.status = status;
    if (source) params.source = source;
    if (jobId) params.job_id = jobId;
    if (vendor.trim()) params.vendor = vendor.trim();
    api.get("/contractor/accounting/queue", { params }).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [status, source, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    api.get("/contractor/settings").then((r) => setAccountingMode(r.data.accounting_mode || "export_only"));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [vendor]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const jobLabel = (id) => {
    const j = jobs.find((j2) => j2.id === id);
    return j ? j.code : "—";
  };
  const isQboMode = accountingMode !== "export_only";

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      const { data: result } = await api.post("/contractor/accounting/sync-all");
      if (result.failed > 0) {
        toast.info(`Synced ${result.synced}, ${result.failed} failed — see Sync Issues`);
      } else {
        toast.success(`Synced ${result.synced} transaction${result.synced === 1 ? "" : "s"} to QuickBooks`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch sync failed");
    } finally {
      setSyncingAll(false);
    }
  };

  // In export_only mode "synced" really means "exported" — same status,
  // adapted label (Package 18).
  const statusBadge = (s) =>
    !isQboMode && s === "synced"
      ? <span className="ss-pill ss-pill-green" data-testid="status-exported">Exported</span>
      : <StatusBadge status={s} />;

  return (
    <div className="p-6 md:p-8" data-testid="accounting-queue-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5">
            <ShieldCheck size={22} className="text-sky-500" /> Accounting Review
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isQboMode ? "Nothing syncs to QuickBooks without explicit accounting approval." : "Approved transactions export as structured CSV/XLSX batches."}
          </p>
        </div>
        {view === "queue" && isQboMode && data.counts.ready_for_sync > 0 && (
          <button onClick={syncAll} disabled={syncingAll} data-testid="sync-all-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2CA01C] hover:bg-[#248217] text-white text-[13px] font-semibold disabled:opacity-60">
            <UploadCloud size={14} /> {syncingAll ? "Syncing…" : `Sync All Ready (${data.counts.ready_for_sync})`}
          </button>
        )}
      </div>

      <div className="flex gap-1.5 mt-5">
        {[["queue", "Queue"], ...(isQboMode ? [["issues", "Sync Issues"]] : []), ["export", "Export"]].map(([v, label]) => (
          <button
            key={v} onClick={() => setView(v)} data-testid={`accounting-view-${v}`}
            className={`px-3.5 h-9 rounded-lg text-[12.5px] font-semibold ${view === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === "issues" && (
        <SyncIssues key={issuesKey} jobLabel={jobLabel} onRetried={load} onFix={(i) => setSelected({ ...i, startEditing: true })} />
      )}
      {view === "export" && <ExportPanel isQboMode={isQboMode} />}

      {view === "queue" && (<>
      <div className="flex gap-2 mt-4 border-b border-slate-100 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.value || "all"} onClick={() => setStatus(t.value)} data-testid={`accounting-tab-${t.value || "all"}`}
            className={`px-3.5 py-2 text-[12.5px] font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${status === t.value ? "border-sky-500 text-sky-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            {(!isQboMode && t.value === "synced" ? "Exported" : t.label)}{t.value && data.counts[t.value] > 0 ? ` (${data.counts[t.value]})` : ""}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <SearchableSelect value={source} onChange={(e) => setSource(e.target.value)} data-testid="accounting-source-filter" className="h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]">
          <option value="">All sources</option>
          <option value="invoice">Vendor Invoices</option>
          <option value="po">Purchase Orders</option>
          <option value="outside_purchase">Expense Log</option>
          <option value="corpay">CorPay</option>
          <option value="ramp">Ramp</option>
          <option value="csv">CSV Import</option>
        </SearchableSelect>
        <SearchableSelect value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="accounting-job-filter" className="h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]">
          <option value="">All jobs</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
        </SearchableSelect>
        <input
          value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Filter by vendor…"
          data-testid="accounting-vendor-filter" className="h-9 px-3 rounded-lg border border-slate-200 text-[12.5px] w-48"
        />
      </div>

      <div className="overflow-x-auto mt-4">
        <table className="w-full text-[13px]" data-testid="accounting-queue-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2">Transaction</th>
              <th className="text-left py-2">Vendor</th>
              <th className="text-left py-2">Job</th>
              <th className="text-left py-2">Source</th>
              <th className="text-left py-2">Date</th>
              <th className="text-right py-2">Amount</th>
              <th className="text-left py-2 pl-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((i) => (
              <tr key={`${i.type}-${i.id}`} onClick={() => setSelected(i)} className="cursor-pointer hover:bg-slate-50" data-testid={`accounting-row-${i.label.replace(/[^a-zA-Z0-9-]/g, "_")}`}>
                <td className="py-2.5 border-t border-slate-100 mono text-sky-600 font-medium">{i.label}</td>
                <td className="py-2.5 border-t border-slate-100">{i.vendor_name}</td>
                <td className="py-2.5 border-t border-slate-100 text-slate-500">{i.job_id ? jobLabel(i.job_id) : "—"}</td>
                <td className="py-2.5 border-t border-slate-100">
                  <span className="ss-pill ss-pill-slate">{SOURCE_LABELS[i.source] || i.source}</span>
                  {i.match_status === "discrepancy" && <span className="ss-pill ss-pill-amber ml-1" data-testid={`match-flag-${i.id}`}>Match ⚠</span>}
                  {i.match_status === "matched" && <span className="ss-pill ss-pill-green ml-1">Matched</span>}
                </td>
                <td className="py-2.5 border-t border-slate-100 text-slate-500">{i.date || "—"}</td>
                <td className="py-2.5 border-t border-slate-100 text-right mono font-medium">{fmt(i.amount)}</td>
                <td className="py-2.5 border-t border-slate-100 pl-3">
                  <span className="flex items-center gap-1.5 flex-wrap">
                    {statusBadge(i.accounting_status)}
                    {i.needs_credit && <StatusBadge status="received_with_issues" testId={`needs-credit-${i.id}`} />}
                  </span>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-slate-400">Nothing here.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </>)}

      <AccountingReviewDrawer
        item={selected}
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        onChanged={() => { load(); setIssuesKey((k) => k + 1); }}
        isQboMode={isQboMode}
        startEditing={!!selected?.startEditing}
      />
    </div>
  );
}

export function SyncIssues({ jobLabel, onRetried, onFix }) {
  const [issues, setIssues] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const load = () => api.get("/contractor/accounting/sync-issues").then((r) => setIssues(r.data));
  useEffect(() => { load(); }, []);

  const retry = async (issue) => {
    setRetrying(issue.id);
    try {
      const { data } = await api.post(`/contractor/accounting/${issue.type}/${issue.id}/sync`);
      if (data.ok) {
        toast.success(`${issue.label} synced to QuickBooks`);
      } else {
        toast.error(data.error);
      }
      load();
      onRetried?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  if (!issues) return <div className="mt-6 text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="mt-5" data-testid="sync-issues-panel">
      {issues.length === 0 && <div className="text-[13px] text-slate-400 py-8 text-center">No sync issues — everything that tried to sync made it.</div>}
      <div className="space-y-3">
        {issues.map((i) => (
          <div key={`${i.type}-${i.id}`} className="border border-red-100 bg-red-50/40 rounded-xl p-4" data-testid={`sync-issue-${i.label.replace(/[^a-zA-Z0-9-]/g, "_")}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-[13px] font-semibold">
                <AlertTriangle size={14} className="text-red-500" /> {i.label}
                <span className="text-slate-400 font-normal">· {i.vendor_name} · {fmt(i.amount)}{i.job_id ? ` · ${jobLabel(i.job_id)}` : ""}</span>
              </div>
              <div className="flex gap-2">
              {i.type !== "vendor_credit" && (
                <button onClick={() => onFix(i)} data-testid={`fix-sync-${i.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold hover:bg-slate-50">
                  <Pencil size={12} /> Fix cost codes
                </button>
              )}
              <button onClick={() => retry(i)} disabled={retrying === i.id} data-testid={`retry-sync-${i.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold hover:bg-slate-50 disabled:opacity-60">
                <RotateCw size={12} /> {retrying === i.id ? "Retrying…" : "Retry Sync"}
              </button>
              </div>
            </div>
            <div className="text-[12.5px] text-red-800 mt-2">{i.sync_error.message}</div>
            <div className="text-[11px] text-slate-400 mt-1">Failed {fmtDateTime(i.sync_error.at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ExportPanel({ isQboMode }) {
  const [status, setStatus] = useState("ready_for_sync");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [format, setFormat] = useState("csv");
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const params = () => {
    const p = { status };
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  };

  const loadBatches = () => api.get("/contractor/accounting/export/batches").then((r) => setBatches(r.data));
  useEffect(() => { loadBatches(); }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/contractor/accounting/export/preview", { params: params() }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
  }, [status, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/contractor/accounting/export", { params: { ...params(), format }, responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="([^"]+)"/);
      saveBlob(res.data, match ? match[1] : `accounting-export.${format}`);
      toast.success("Export downloaded — transactions marked exported");
      loadBatches();
      api.get("/contractor/accounting/export/preview", { params: params() }).then((r) => setPreview(r.data));
    } catch (e) {
      if (e.response?.status === 404) toast.error("No transactions match the selected filters.");
      else toast.error("Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const downloadBatch = async (batch) => {
    const res = await api.get(`/contractor/accounting/export/batches/${batch.id}/download`, { responseType: "blob" });
    saveBlob(res.data, `scapesource-accounting-batch-${batch.id.slice(-6)}.${batch.format}`);
  };

  return (
    <div className="mt-5" data-testid="export-panel">
      <p className="text-[12.5px] text-slate-500 mb-4 max-w-3xl">
        A spreadsheet of every transaction that can go to QuickBooks — vendor invoices, Expense Log entries, card transactions, vendor credits and POs — one row per line, with job, cost code and account.{" "}
        {isQboMode
          ? "Exporting doesn't change anything: they still sync to QuickBooks as usual."
          : "Exporting marks them exported, since there's no QuickBooks sync."}
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Status</label>
          <SearchableSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="export-status-select" className="mt-1 block h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]">
            <option value="ready_for_sync">Ready for {isQboMode ? "Sync" : "Export"}</option>
            <option value="synced">{isQboMode ? "Synced" : "Exported"}</option>
            <option value="pending_review">Pending Review</option>
          </SearchableSelect>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="export-date-from" className="mt-1 block h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="export-date-to" className="mt-1 block h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Format</label>
          <SearchableSelect value={format} onChange={(e) => setFormat(e.target.value)} data-testid="export-format-select" className="mt-1 block h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]">
            <option value="csv">CSV</option>
            <option value="xlsx">XLSX</option>
          </SearchableSelect>
        </div>
        <button onClick={download} disabled={downloading || !preview || preview.transaction_count === 0} data-testid="export-download-button" className="flex items-center gap-2 px-4 h-9 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[12.5px] font-semibold disabled:opacity-50">
          <Download size={13} /> {downloading ? "Exporting…" : `Export${preview ? ` ${preview.transaction_count}` : ""}`}
        </button>
      </div>

      {preview && preview.already_exported.length > 0 && (
        <div className="mt-4 text-[12.5px] text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3.5 py-3" data-testid="re-export-warning">
          <span className="font-semibold">{preview.already_exported.length} of these {preview.transaction_count} transaction{preview.transaction_count === 1 ? " was" : "s were"} already exported:</span>{" "}
          {preview.already_exported.map((x) => x.label).join(", ")} — exporting again creates a duplicate batch.
        </div>
      )}

      <div className="mt-7">
        <div className="text-[13px] font-bold mb-2">Export History</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="export-batches-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                <th className="text-left py-2">Batch</th>
                <th className="text-left py-2">Exported</th>
                <th className="text-left py-2">By</th>
                <th className="text-right py-2">Transactions</th>
                <th className="text-right py-2">Rows</th>
                <th className="text-left py-2 pl-3">Format</th>
                <th className="text-right py-2" />
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50" data-testid={`export-batch-row-${b.id}`}>
                  <td className="py-2.5 border-t border-slate-100 mono text-slate-500">…{b.id.slice(-6)}</td>
                  <td className="py-2.5 border-t border-slate-100">{fmtDateTime(b.created_at)}</td>
                  <td className="py-2.5 border-t border-slate-100">{b.created_by_name}</td>
                  <td className="py-2.5 border-t border-slate-100 text-right mono">{b.transaction_count}</td>
                  <td className="py-2.5 border-t border-slate-100 text-right mono">{b.row_count}</td>
                  <td className="py-2.5 border-t border-slate-100 pl-3 uppercase text-slate-500">{b.format}</td>
                  <td className="py-2.5 border-t border-slate-100 text-right">
                    <button onClick={() => downloadBatch(b)} data-testid={`download-batch-${b.id}`} className="text-sky-600 font-semibold">Download</button>
                  </td>
                </tr>
              ))}
              {batches.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-400">No exports yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
